import type { Person } from './api';

export type TabKey = 'proserve' | 'direct' | 'partner' | 'caylent';

/** Tab from the person's OWN evidence (source_tab, pid prefix, title markers) — null when the
 *  person carries none. The caller inherits a missing tab from the manager chain before the
 *  terminal ProServe default (lesson 4, 2026-07-19: nobody defaults into an org). */
// The header and graph share classification without making the header build the full graph.
export function inferTabEvidence(p: Person): TabKey | null | 'hidden' {
  // Prefer the canonical source_tab (set by the extractor when known).
  // Fall back to pid-prefix heuristic for anything without it (e.g. people
  // added via Bedrock ingestion, which don't carry a source-HTML tab).
  // Admins and other 'hidden' rows are filtered out upstream, but guard
  // here too so a stray one doesn't land in ProServe.
  const st = (p.source_tab ?? '').toLowerCase();
  if (st === 'hidden') return 'hidden';
  if (st === 'proserve' || st === 'direct' || st === 'partner' || st === 'caylent') {
    return st as TabKey;
  }

  const id = p.pid.toLowerCase();
  if (id.startsWith('ds-')) return 'direct';
  if (id.startsWith('ps-')) return 'partner';
  if (id.startsWith('cy-') || id.includes('.')) return 'caylent';

  // The person's own title org-path markers (2026-07-19: a whole AGS sales batch rendered under
  // the ProServe header because Bedrock-ingested people carry no source_tab and this function
  // defaulted them). Mirrors the backend Wolf derivation — AGS = Global Sales (partner when the
  // path says Partner/PSM, else direct); ProServe names itself.
  const role = p.role ?? '';
  // Coded org-path first (mirrors the backend Wolf: "ProServe liaison, Field-AGS-…" is an AGS
  // person — the path outranks loose title words), then whole-title markers.
  if (/(^|[\s,(])(?:HQ|Field)-AGS-/.test(role)) return /\bPartner\b|(^|[\s,-])PSM([\s,-]|$)/i.test(role) ? 'partner' : 'direct';
  // A coded official title kept as the alternate title, because the owner's plain-words role replaced it (B288),
  // still places the person by its org path.
  const title = p.title ?? '';
  if (/(^|[\s,(])(?:HQ|Field)-AGS-/.test(title)) return /\bPartner\b|(^|[\s,-])PSM([\s,-]|$)/i.test(title) ? 'partner' : 'direct';
  // ProServe names itself — including its service-org acronyms (owner fact-check 2026-07-20 vs the
  // reference chart: 82/105 ProServe roles carry "ProServe", the rest carry GS/PS/PPL prefixes,
  // and 0/346 Direct roles carry any of them). Checked BEFORE Partner: "GS Partner Dev Mgr" is a
  // ProServe partner-facing role, not Partner Sales.
  if (/ProServe|Professional Services/i.test(role)) return 'proserve';
  if (/(^|[\s,(-])(?:GS|PS|PPL)([\s,)-]|$)/.test(role)) return 'proserve';
  if (/(^|[\s,(-])AGS([\s)-]|$)/.test(role)) {
    return /\bPartner\b|(^|[\s,-])PSM([\s,-]|$)/i.test(role) ? 'partner' : 'direct';
  }
  // Partner Sales titles name themselves too ("Partner Sales Manager", "Channel & Partner Sales").
  if (/\bPartner\b|(^|[\s,-])PSM([\s,-]|$)/i.test(role)) return 'partner';
  // Direct-sales vocabulary (reference: ISV in 197/346, Sales Rep 86, Account Manager 50,
  // Sr. Leader 11, Greenfield 10 — and none of it appears on the ProServe tab).
  if (/\bISV\b|Sales Rep|Account Manager|Sr\.? Leader|Greenfield/i.test(role)) return 'direct';
  return null;
}

export function buildTabMembership(people: readonly Person[]) {
  const byPid = new Map<string, Person>();
  for (const p of people) byPid.set(p.pid, p);

  const tabOf = new Map<string, TabKey>();
  const noEvidence: Person[] = [];
  for (const p of people) {
    const tab = inferTabEvidence(p);
    if (tab === 'hidden') continue;
    if (tab) tabOf.set(p.pid, tab);
    else noEvidence.push(p);
  }
  // Inherit a missing tab from the manager chain (lesson 4: nobody defaults into an org).
  // The truly evidence-less get NO tab (owner, 2026-07-20: "people landed in proserve and there
  // are no proserve people in this upload" — the old terminal `?? 'proserve'` silently mis-shelved
  // them). They surface in the visible Unplaced bucket instead, until decision-3's inferred
  // placement lands them properly.
  const unplacedPids: string[] = [];
  for (const p of noEvidence) {
    let cur = p.manager_pid;
    let hops = 0;
    let inherited: TabKey | null = null;
    while (cur && hops < 20) {
      const t = tabOf.get(cur);
      if (t) {
        inherited = t;
        break;
      }
      cur = byPid.get(cur)?.manager_pid ?? null;
      hops++;
    }
    if (inherited) tabOf.set(p.pid, inherited);
    else unplacedPids.push(p.pid);
  }

  return { byPid, tabOf, unplacedPids };
}
